import React, {FC} from 'react';
import styled from 'styled-components';

import noResultsImageSrc from '../../../../../../../../src/assets/images/noResults.svg';
import {contactsLearnMoreUrl} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {ExportCardsJobOptionTypesEnum} from '../../../../../../../../src/models/api/jobModel';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CreateContactButtonContainer} from '../../common/buttons/createContactButtonContainer';
import {ImportCSVButton} from '../../common/buttons/importCSVButton';
import {InfoButton} from '../../common/buttons/infoButton';
import {PrivateContactsInfoBoxes} from '../../common/infobox/privateContactsInfoBoxes';
import {VerticalRule} from '../../common/verticalRule';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {ContactsManagerHeader} from '../../contactsManagerHeader';
import {ImportContactsCSVDialogContainer} from '../../dialogs/import/contacts/importContactsCSVDialogContainer';
import {ExportContactsPopoverContainer} from '../../exportContacts/exportContactsPopoverContainer';
import {buildInfoBoxToggleLabel} from '../../helpers/infoBoxHelpers';
import {ContactsTableMultiSelectActions} from '../../tables/common/contacts/contactsTableMultiSelectActions';
import {ContactsManagerTableEmptyState} from '../../tables/common/contactsManagerEmptyState';
import {ContactsTableContainer} from '../../tables/contacts/contactsTableContainer';

/*
 * Props.
 */

interface PrivateContactsPageProps {
  isShowingImportCSVDialog: boolean;
  hasRunningImportContactsCSVJobs: boolean;
  namespace: string;
  isInfoBoxExpanded: boolean;
  selectedCardIds: ReadonlyArray<number>;
  onToggleInfoBox: Handler;
  onToggleImportCSVClick: Handler;
}

/*
 * Styles.
 */

const StyledImg = styled.img`
  width: 92px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  height: inherit;
`;

/*
 * Component.
 */

export const PrivateContactsPage: FC<PrivateContactsPageProps> = (props) => {
  const {
    selectedCardIds,
    namespace,
    isInfoBoxExpanded,
    hasRunningImportContactsCSVJobs,
    isShowingImportCSVDialog,
    onToggleInfoBox,
    onToggleImportCSVClick,
  } = props;
  const {panelId} = useContactsManagerContext();

  return (
    <>
      <ContactsManagerHeader
        title={<Trans id="private-contacts-table-title">Private contacts</Trans>}
        actions={
          <>
            <ExportContactsPopoverContainer defaultExportType={ExportCardsJobOptionTypesEnum.PRIVATE} />
            <CreateContactButtonContainer />
            <ImportCSVButton onClick={onToggleImportCSVClick} isDisabled={hasRunningImportContactsCSVJobs} />
            <VerticalRule />
            <InfoButton
              label={buildInfoBoxToggleLabel()}
              onToggle={onToggleInfoBox}
              isActive={isInfoBoxExpanded}
            />
          </>
        }
      />
      <StyledWrapperDiv>
        <ContactsTableContainer
          panelId={panelId}
          renderEmpty={() => renderEmptyState(onToggleImportCSVClick)}
          queryOptions={{namespace}}
          multiSelectHeaderActions={
            <ContactsTableMultiSelectActions
              panelId={panelId}
              isAddToListsButtonVisible
              cardIds={selectedCardIds}
              namespace={namespace}
            />
          }
          shouldHideAccountsColumn
        />
        {isInfoBoxExpanded && <PrivateContactsInfoBoxes />}
      </StyledWrapperDiv>
      {isShowingImportCSVDialog && (
        <ImportContactsCSVDialogContainer panelId={panelId} onRequestClose={onToggleImportCSVClick} />
      )}
    </>
  );
};

function renderEmptyState(onToggleImportCSVClick: Handler) {
  return (
    <ContactsManagerTableEmptyState
      title={<Trans id="private-contacts-table-empty-state-title">No private contacts</Trans>}
      image={<StyledImg alt="private-contacts-table-empty-state-image" src={noResultsImageSrc} />}
      description={
        <Trans id="private-contacts-table-empty-state-description">
          Private Contacts allow you to manage contact information that you prefer to keep private.
        </Trans>
      }
      learnMoreUrl={contactsLearnMoreUrl}
    >
      <CreateContactButtonContainer />
      <ImportCSVButton onClick={onToggleImportCSVClick} isDisabled={false} />
    </ContactsManagerTableEmptyState>
  );
}
